import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSatisfiedCounter } from "../../store/slices/satisfiedCounter";
import "./StatsSection.css";
/**
 * Animated counter hook with ease-out-expo easing.
 * @param {number} finalValue - Target number to count up to.
 * @param {number} duration - Animation duration in ms.
 * @param {number} delay - Delay before animation starts (ms).
 * @returns {number} Current animated count.
 */
export const useAnimatedCounter = (finalValue, duration = 2000, delay = 0) => {
  const [count, setCount] = useState(0);
  const frameRef = useRef(null);
  const startTimeRef = useRef(null);
  const finalValueRef = useRef(finalValue);
  const isMountedRef = useRef(true);

  // Update ref when finalValue changes
  useEffect(() => {
    finalValueRef.current = finalValue;
  }, [finalValue]);

  const animate = useCallback(() => {
    if (!startTimeRef.current) {
      startTimeRef.current = performance.now();
    }
    const elapsed = performance.now() - startTimeRef.current;
    const progress = Math.min(elapsed / duration, 1);
    // easeOutExpo easing
    const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
    const current = Math.floor(eased * finalValueRef.current);

    if (isMountedRef.current) {
      setCount(current);
    }

    if (progress < 1) {
      frameRef.current = requestAnimationFrame(animate);
    } else if (isMountedRef.current) {
      setCount(finalValueRef.current);
      frameRef.current = null;
    }
  }, [duration]);

  useEffect(() => {
    isMountedRef.current = true;
    startTimeRef.current = null;
    const timer = setTimeout(() => {
      // Avoid restarting if already animating
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      startTimeRef.current = null;
      frameRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [animate, delay]);

  return count;
};

/**
 * Single stat item card.
 */
const StatItem = React.memo(
  ({ label, targetValue, suffix = "", onClick, isLoading }) => {
    const animatedValue = useAnimatedCounter(targetValue, 2000, 0);

    const displayValue = useMemo(() => {
      if (isLoading) return null;
      return `${animatedValue.toLocaleString("en-IN")}${suffix}`;
    }, [animatedValue, suffix, isLoading]);

    return (
      <div
        className={`glass-stat-card ${onClick ? "is-clickable" : ""}`}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => e.key === "Enter" && onClick(e) : undefined}
      >
        <div className="stat-number">
          {isLoading ? (
            <span className="loading-spinner" aria-label="Loading...">
              ...
            </span>
          ) : (
            displayValue
          )}
        </div>
        <div className="stat-label">{label}</div>
        <div className="stat-footer-line">
          <div className="line-fill" />
        </div>
      </div>
    );
  },
);

/**
 * Stats section that fetches live data and displays animated counters.
 */
const StatsSection = React.memo(() => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(
    (state) => state.satisfiedCounter,
  );
  const [targets, setTargets] = useState({
    yearsExp: 28,
    partners: 250,
    freelancers: 5001,
    customers: 50002,
  });

  // Safely parse API data
  useEffect(() => {
    if (!data?.success || !data?.data) return;

    const api = data.data;
    let freelancers = 5001;
    let customers = 50002;

    try {
      if (api.freelancers?.current) {
        freelancers = parseInt(api.freelancers.current.replace(/,/g, ""), 10);
        if (isNaN(freelancers)) freelancers = 5001;
      }
      if (api.satisfied_customers?.current) {
        customers = parseInt(
          api.satisfied_customers.current.replace(/,/g, ""),
          10,
        );
        if (isNaN(customers)) customers = 50002;
      }
    } catch (err) {
      console.error("Error parsing counter data:", err);
    }

    setTargets({
      yearsExp: api.years_exp_detailed?.value || 28,
      partners: 250, // static value, not from API
      freelancers,
      customers,
    });
  }, [data]);

  // Fetch on mount and poll every 2 minutes
  useEffect(() => {
    dispatch(fetchSatisfiedCounter());
    const interval = setInterval(
      () => {
        dispatch(fetchSatisfiedCounter());
      },
      2 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [dispatch]);

  // Memoize open handlers
  const openYearsExp = useCallback(
    () => window.open("/year-experience", "_blank"),
    [],
  );
  const openPartners = useCallback(
    () => window.open("/banking-partners", "_blank"),
    [],
  );
  const openFreelancers = useCallback(
    () => window.open("/freelancers", "_blank"),
    [],
  );
  const openCustomers = useCallback(
    () => window.open("/satisfied-customers", "_blank"),
    [],
  );

  if (error) {
    // Fallback: show static values but still render (silent error)
    console.warn("Failed to fetch stats:", error);
  }

  return (
    <section className="main-stats-container">
      <div className="responsive-stats-grid">
        <StatItem
          label="Years Experience"
          targetValue={targets.yearsExp}
          suffix="+"
          onClick={openYearsExp}
          isLoading={loading}
        />
        <StatItem
          label="Banking Partners"
          targetValue={targets.partners}
          suffix="+"
          onClick={openPartners}
          isLoading={loading}
        />
        <StatItem
          label="Freelancers"
          targetValue={targets.freelancers}
          suffix="+"
          onClick={openFreelancers}
          isLoading={loading}
        />
        <StatItem
          label="Satisfied Customers"
          targetValue={targets.customers}
          suffix="+"
          onClick={openCustomers}
          isLoading={loading}
        />
      </div>
    </section>
  );
});

export default StatsSection;
